import { APP } from 'src/app/config/app';
import { ITimelineColumn, ITimelineSegment } from 'src/app/models';

function cycle(value: number, length: number): number {
  return ((value % length) + length) % length;
}

function hourOfDay(minutes: number): number {
  return cycle(Math.floor(minutes / APP.MINUTES_PER_HOUR), APP.SCHEDULE_ALL_HOURS.length);
}

function dayOfWeek(minutes: number): number {
  return cycle(Math.floor(minutes / APP.MINUTES_PER_DAY), APP.SCHEDULE_ALL_WEEKDAYS.length);
}

function normalizeSelection(values: unknown, all: number[]): number[] {
  const valid = Array.isArray(values) ? all.filter((value) => values.includes(value)) : [];
  return valid.length ? valid : all;
}

export function normalizeVisibleHours(hours: unknown): number[] {
  return normalizeSelection(hours, APP.SCHEDULE_ALL_HOURS);
}

export function normalizeVisibleDays(days: unknown): number[] {
  return normalizeSelection(days, APP.SCHEDULE_ALL_WEEKDAYS);
}

export function sameSelection(first: number[], second: number[]): boolean {
  return first.length === second.length && first.every((value, index) => value === second[index]);
}

export function toggleSelection(selection: number[], value: number, all: number[]): number[] {
  return selection.includes(value)
    ? selection.filter((selected) => selected !== value)
    : all.filter((candidate) => candidate === value || selection.includes(candidate));
}

function isHidden(minutes: number, visibleHours: number[], visibleDays: number[]): boolean {
  return !visibleHours.includes(hourOfDay(minutes)) || !visibleDays.includes(dayOfWeek(minutes));
}

export function hiddenRunsPerWeek(visibleHours: number[], visibleDays: number[]): number {
  const hour = APP.MINUTES_PER_HOUR;
  const starts = Array.from({ length: APP.MINUTES_PER_WEEK / hour }, (_, index) => index * hour);
  return starts.filter(
    (start) => isHidden(start, visibleHours, visibleDays) && !isHidden(start - hour, visibleHours, visibleDays),
  ).length;
}

export function buildTimelineSegments(
  axisStart: number,
  axisEnd: number,
  visibleHours: number[],
  pxPerMinute: number,
  visibleDays = APP.SCHEDULE_ALL_WEEKDAYS,
): ITimelineSegment[] {
  const segments: ITimelineSegment[] = [];
  for (let start = axisStart; start < axisEnd; start += APP.MINUTES_PER_HOUR) {
    const collapsed = isHidden(start, visibleHours, visibleDays);
    const last = segments[segments.length - 1];
    if (last?.collapsed === collapsed) {
      last.endMinutes += APP.MINUTES_PER_HOUR;
      last.width += collapsed ? 0 : APP.MINUTES_PER_HOUR * pxPerMinute;
      continue;
    }
    segments.push({
      startMinutes: start,
      endMinutes: start + APP.MINUTES_PER_HOUR,
      left: last ? last.left + last.width : 0,
      width: collapsed ? APP.SCHEDULE_COLLAPSED_HOURS_WIDTH_PX : APP.MINUTES_PER_HOUR * pxPerMinute,
      collapsed,
    });
  }
  return segments;
}

export function segmentsWidth(segments: ITimelineSegment[]): number {
  const last = segments[segments.length - 1];
  return last ? last.left + last.width : 0;
}

export function minutesToPx(segments: ITimelineSegment[], minutes: number): number {
  const segment = segments.find((candidate) => minutes < candidate.endMinutes) ?? segments[segments.length - 1];
  if (!segment) {
    return 0;
  }
  const duration = segment.endMinutes - segment.startMinutes;
  const offset = Math.min(Math.max(minutes - segment.startMinutes, 0), duration);
  return segment.left + (offset / duration) * segment.width;
}

export function pxToMinutes(segments: ITimelineSegment[], px: number): number {
  const segment = segments.find((candidate) => px < candidate.left + candidate.width) ?? segments[segments.length - 1];
  if (!segment) {
    return 0;
  }
  const offset = Math.min(Math.max(px - segment.left, 0), segment.width);
  return segment.startMinutes + (segment.width ? offset / segment.width : 0) * (segment.endMinutes - segment.startMinutes);
}

export function startsCollapsed(segments: ITimelineSegment[], minutes: number): boolean {
  return segments.some((segment) => segment.collapsed && minutes >= segment.startMinutes && minutes < segment.endMinutes);
}

export function endsCollapsed(segments: ITimelineSegment[], minutes: number): boolean {
  return segments.some((segment) => segment.collapsed && minutes > segment.startMinutes && minutes <= segment.endMinutes);
}

export function visibleSpans(segments: ITimelineSegment[], startMinutes: number, endMinutes: number): ITimelineSegment[] {
  return segments
    .filter((segment) => !segment.collapsed && startMinutes < segment.endMinutes && endMinutes > segment.startMinutes)
    .map((segment) => {
      const from = Math.max(startMinutes, segment.startMinutes);
      const to = Math.min(endMinutes, segment.endMinutes);
      const left = minutesToPx([segment], from);
      return { startMinutes: from, endMinutes: to, left, width: minutesToPx([segment], to) - left, collapsed: false };
    });
}

export function buildTimelineColumns(
  segments: ITimelineSegment[],
  columnMinutes: number,
  periodMinutes: number,
  label: (columnStart: number) => string,
): ITimelineColumn[] {
  const columns: ITimelineColumn[] = [];
  let labelledColumn: number | undefined;
  for (const segment of segments) {
    if (segment.collapsed) {
      const crossesPeriod = Math.floor(segment.endMinutes / periodMinutes) * periodMinutes > segment.startMinutes;
      columns.push({ ...segment, label: '', outside: false, boundary: crossesPeriod });
      continue;
    }
    for (let start = segment.startMinutes; start < segment.endMinutes; ) {
      const columnStart = Math.floor(start / columnMinutes) * columnMinutes;
      const end = Math.min(columnStart + columnMinutes, segment.endMinutes);
      const left = minutesToPx([segment], start);
      columns.push({
        startMinutes: start,
        endMinutes: end,
        left,
        width: minutesToPx([segment], end) - left,
        collapsed: false,
        label: columnStart === labelledColumn ? '' : label(columnStart),
        outside: start < 0 || start >= periodMinutes,
        boundary: end % periodMinutes === 0,
      });
      labelledColumn = columnStart;
      start = end;
    }
  }
  return columns;
}
